import * as React from "react";
import { DensityProvider } from "./providers/DensityProvider";
import { LocaleProvider } from "./providers/LocaleProvider";
import { QueryProvider } from "./providers/QueryProvider";
import { TenantProvider } from "./providers/TenantProvider";
import { ThemeProvider } from "./providers/ThemeProvider";
import { DesktopAuthProvider } from "./lib/supabase/AuthProvider";

/**
 * Route-Detection (W1):
 *   Default → BootSmoke
 *   ?view=showcase OR /showcase → ShowcasePage (Living-Documentation)
 *   ?view=app      OR /app      → AppShell (W1 — production layout)
 *   ?view=orb-lab  OR /orb-lab  → OrbLabPage (isolated Bio.ORB + ARC playground)
 *
 * Top-Routes sind React.lazy → eigener Chunk pro Route. Initial-Load
 * bekommt nur die Route die der User ansteuert.
 */
const APP_HASH_ROUTES = new Set([
  "dashboard",
  "today",
  "preview",
  "briefing",
  "trends",
  "prediction",
  "analyse",
  "recovery",
  "bio-age",
  "bioage",
  "transformation",
  "stack",
  "supplements",
  "peptides",
  "rx-pharma",
  "protocol",
  "activities",
  "sleep",
  "hrv",
  "steps",
  "nutrition-planner",
  "nutrition",
  "bloodwork",
  "lab",
  "journal",
  "inbox",
  "docs",
  "vault",
  "settings",
  "account",
  "privacy",
  "agent",
  "chat",
  "hermes",
]);

export function detectRoute(): "showcase" | "boot-smoke" | "app" | "orb-lab" {
  if (typeof window === "undefined") return "boot-smoke";
  const pathname = window.location.pathname;
  if (pathname.startsWith("/orb-lab") || pathname.startsWith("/_orb-lab")) {
    return "orb-lab";
  }
  if (pathname.startsWith("/showcase") || pathname.startsWith("/_showcase")) {
    return "showcase";
  }
  if (pathname.startsWith("/app")) {
    return "app";
  }
  const view = new URLSearchParams(window.location.search).get("view");
  if (view === "orb-lab") return "orb-lab";
  if (view === "showcase") return "showcase";
  if (view === "app") return "app";
  const hashRoute = window.location.hash.replace(/^#\/?/, "").split("?")[0];
  if (hashRoute && APP_HASH_ROUTES.has(hashRoute)) return "app";
  return "boot-smoke";
}

const BootSmoke = React.lazy(() =>
  import("./components/BootSmoke").then((m) => ({ default: m.BootSmoke })),
);
const ShowcasePage = React.lazy(() =>
  import("./showcase/ShowcasePage").then((m) => ({ default: m.ShowcasePage })),
);
const AppShell = React.lazy(() =>
  import("./shell/AppShell").then((m) => ({ default: m.AppShell })),
);
const OrbLabPage = React.lazy(() =>
  import("./orb-lab/OrbLabPage").then((m) => ({ default: m.OrbLabPage })),
);

/**
 * App — Provider-Chain order is intentional:
 *   TenantProvider → ThemeProvider → LocaleProvider → DensityProvider → QueryProvider
 *
 * Theme depends on Tenant (Salutem forces medic).
 * Locale depends on both Tenant + Theme (medic-coupling, salutem-default).
 * Density is independent (W0.8.9).
 */
export function App() {
  const route = detectRoute();

  return (
    <TenantProvider>
      <ThemeProvider>
        <LocaleProvider>
          <DensityProvider>
            <QueryProvider>
              <DesktopAuthProvider>
                <React.Suspense fallback={<RouteLoadingScreen />}>
                  {route === "app" ? (
                    <AppShell />
                  ) : route === "orb-lab" ? (
                    <OrbLabPage />
                  ) : route === "showcase" ? (
                    <ShowcasePage />
                  ) : (
                    <BootSmoke />
                  )}
                </React.Suspense>
              </DesktopAuthProvider>
            </QueryProvider>
          </DensityProvider>
        </LocaleProvider>
      </ThemeProvider>
    </TenantProvider>
  );
}

/**
 * RouteLoadingScreen — minimal initial-load splash.
 * Wird sehr kurz gezeigt während der Route-Chunk lädt.
 */
function RouteLoadingScreen() {
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: "var(--color-bg)" }}
    >
      <div
        className="text-[10px] font-mono uppercase"
        style={{ color: "var(--color-text-subtle)" }}
      >
        ARES Bio.OS · Loading…
      </div>
    </div>
  );
}
